import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { useMarketplace } from '../../context/MarketplaceContext';
import { useCoins } from '../../context/CoinContext';
import { useChat } from '../../context/ChatContext';
import { isMarketplaceUser } from '../../utils/roleManager';
import { checkCoinRequirement, MARKETPLACE_FEES } from '../../utils/marketplaceFees';
import ProductCard from './ProductCard';
import ProductListCard from './ProductListCard';
import LevelBadge from './LevelBadge';
import ProductImageUpload from './ProductImageUpload';
import StoreSettingsForm from './StoreSettingsForm';
import PaymentMethodsDisplay from './PaymentMethodsDisplay';
import PaymentProofViewer from './PaymentProofViewer';
import TrackingNumberInput from './TrackingNumberInput';
import StoreLabelPrintModal from './StoreLabelPrintModal';
import ShippingLabelPrintModal from './ShippingLabelPrintModal';
import PackingSlipPrintModal from './PackingSlipPrintModal';
import VideoCallRequestsList from './VideoCallRequestsList';
import VideoCallRequestBadge from './VideoCallRequestBadge';
import { VideoCallIcon } from '../Layout/ProfileMenuDropdown';
import { useNavigate, useSearchParams } from 'react-router-dom';

const StoreManagement = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { openStore, updateStore, listProduct, createAuction, createBuyerRequest, getStoreSubscription } = useMarketplace();
  const { coins } = useCoins();
  const { chats, messages: contextMessages } = useChat();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [store, setStore] = useState(null);
  const [packageType, setPackageType] = useState('free');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('products'); // products, orders, chat, auctions, buyerRequests, settings
  const [chatFilter, setChatFilter] = useState('all'); // all, replied, unreplied
  const [chatSubTab, setChatSubTab] = useState('chats'); // chats, videoCallRequests
  const [pinnedChats, setPinnedChats] = useState([]);
  const [productCache, setProductCache] = useState({}); // Cache for product data
  const [showCreateAuctionForm, setShowCreateAuctionForm] = useState(false);
  const [showStoreLabelModal, setShowStoreLabelModal] = useState(false);
  const [showShippingLabelModal, setShowShippingLabelModal] = useState(false);
  const [showPackingSlipModal, setShowPackingSlipModal] = useState(false);
  const [selectedOrderForPrint, setSelectedOrderForPrint] = useState(null);
  const [selectedOrdersForPackingSlip, setSelectedOrdersForPackingSlip] = useState([]); // สำหรับเลือกหลายออเดอร์
  const [selectedProductForAuction, setSelectedProductForAuction] = useState(null);
  const [auctionForm, setAuctionForm] = useState({
    startingPrice: '',
    reservePrice: '',
    auctionType: 'highest_bid',
    duration: 24
  });
  
  // Form states
  const [showOpenStoreForm, setShowOpenStoreForm] = useState(false);
  const [showAddProductForm, setShowAddProductForm] = useState(false);
  const [storeForm, setStoreForm] = useState({
    name: '',
    description: '',
    category: '',
    contact: { phone: '', email: '', lineId: '', address: '' }
  });
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    quantity: '',
    unit: 'กรัม',
    category: '',
    strain: '',
    type: '',
    images: [],
    minimumOrderQuantity: '', // สั่งซื้อขั้นต่ำ
    paymentMethods: [], // วิธีการชำระเงินที่เลือก
    shippingMethods: [], // วิธีการจัดส่งที่เลือก
    selectedBankAccounts: [], // บัญชีธนาคารที่เลือก (index หรือ id)
    selectedQRCodes: [] // QR Code ที่เลือก (index หรือ id)
  });

  useEffect(() => {
    if (currentUser) {
      fetchStore();
      loadPinnedChats();
    }
  }, [currentUser]);

  // Load product info for chats
  useEffect(() => {
    if (chats && chats.length > 0 && store) {
      loadProductInfoForChats();
    }
  }, [chats, store]);

  // Load product information for product inquiry chats
  const loadProductInfoForChats = async () => {
    const productInquiryChats = chats.filter(chat => 
      chat.type === 'product_inquiry' && chat.productId
    );

    const newProductCache = { ...productCache };

    for (const chat of productInquiryChats) {
      if (chat.productId && !newProductCache[chat.productId]) {
        try {
          const productRef = doc(db, 'products', chat.productId);
          const productSnap = await getDoc(productRef);
          if (productSnap.exists()) {
            newProductCache[chat.productId] = {
              id: productSnap.id,
              ...productSnap.data()
            };
          }
        } catch (error) {
          console.error('Error loading product for chat:', error);
        }
      }
    }

    if (Object.keys(newProductCache).length !== Object.keys(productCache).length) {
      setProductCache(newProductCache);
    }
  };

  // Check URL parameter for tab
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const subTabParam = searchParams.get('subTab');
    
    console.log('🏪 StoreManagement: URL params changed:', {
      tab: tabParam,
      subTab: subTabParam,
      fullURL: window.location.href
    });
    
    if (tabParam && ['products', 'orders', 'chat', 'auctions', 'buyerRequests', 'settings'].includes(tabParam)) {
      console.log('🏪 Setting activeTab to:', tabParam);
      setActiveTab(tabParam);
    }
    
    // Check URL parameter for sub-tab (for chat tab)
    if (subTabParam && ['chats', 'videoCallRequests'].includes(subTabParam)) {
      // If tab is chat or will be set to chat, set the sub-tab
      if (tabParam === 'chat' || (tabParam && tabParam === 'chat')) {
        console.log('🏪 Setting chatSubTab to:', subTabParam);
        setChatSubTab(subTabParam);
      } else if (subTabParam === 'videoCallRequests') {
        // If subTab is videoCallRequests but tab is not chat, set tab to chat first
        console.log('🏪 SubTab is videoCallRequests, setting tab to chat');
        setActiveTab('chat');
        setChatSubTab(subTabParam);
      }
    }
    
    // Check for add product parameter
    const addParam = searchParams.get('add');
    if (addParam === 'true' && tabParam === 'products') {
      setShowAddProductForm(true);
    }
    
    // Check for create auction parameter
    const createParam = searchParams.get('create');
    if (createParam === 'true' && tabParam === 'auctions') {
      setShowCreateAuctionForm(true);
    }
    
    // Check for create buyer request parameter
    if (createParam === 'true' && tabParam === 'buyerRequests') {
      // Open buyer request form if exists
      // This will be handled in the buyer request section
    }
  }, [searchParams]);

  // Load pinned chats from localStorage
  const loadPinnedChats = () => {
    if (currentUser) {
      const saved = localStorage.getItem(`pinnedChats_${currentUser.uid}`);
      if (saved) {
        setPinnedChats(JSON.parse(saved));
      }
    }
  };

  // Save pinned chats to localStorage
  const savePinnedChats = (pinned) => {
    if (currentUser) {
      localStorage.setItem(`pinnedChats_${currentUser.uid}`, JSON.stringify(pinned));
    }
  };

  // Toggle pin chat
  const togglePinChat = (chatId) => {
    const newPinned = pinnedChats.includes(chatId)
      ? pinnedChats.filter(id => id !== chatId)
      : [...pinnedChats, chatId];
    setPinnedChats(newPinned);
    savePinnedChats(newPinned);
  };

  // Get store-related chats
  const getStoreChats = () => {
    if (!store || !chats || chats.length === 0) return [];
    
    // Debug: Log first chat to see its structure
    if (chats.length > 0) {
      console.log('First chat structure:', chats[0]);
      console.log('First chat keys:', Object.keys(chats[0]));
      console.log('First chat has id?', chats[0].id);
      console.log('First chat has _id?', chats[0]._id);
    }
    
    // Filter out chats without ID first
    // Also check for _id as fallback (some Firestore queries might use _id)
    const validChats = chats.filter(chat => {
      if (!chat) return false;
      // Check for id or _id
      const hasId = chat.id || chat._id;
      if (!hasId) {
        console.warn('Chat without ID:', chat);
      }
      return hasId;
    });
    
    // Normalize chat IDs (use _id as id if id doesn't exist)
    const normalizedChats = validChats.map(chat => {
      if (!chat.id && chat._id) {
        return { ...chat, id: chat._id };
      }
      return chat;
    });
    
    if (normalizedChats.length !== chats.length) {
      console.warn(`Filtered out ${chats.length - normalizedChats.length} chats without ID`);
    }
    
    // Filter chats related to this store
    return normalizedChats.filter(chat => {
      // Check if chat is related to this store
      const isStoreChat = chat.storeId === store.id;
      const isProductInquiry = chat.type === 'product_inquiry' && 
                                chat.participants && 
                                chat.participants.includes(currentUser?.uid);
      
      return isStoreChat || isProductInquiry;
    });
  };

  // Check if chat has been replied
  const hasReplied = (chatId) => {
    const chatMessages = contextMessages[chatId] || [];
    if (chatMessages.length === 0) {
      // If no messages loaded, check if store owner is the last message sender from chat data
      const chat = chats.find(c => c.id === chatId);
      if (chat && chat.lastMessageBy) {
        return chat.lastMessageBy === currentUser?.uid;
      }
      return false;
    }
    
    // Check if store owner has sent any message
    return chatMessages.some(msg => msg.senderId === currentUser?.uid);
  };

  // Get filtered chats
  const getFilteredChats = () => {
    let storeChats = getStoreChats();
    
    // Ensure all chats have IDs before processing
    storeChats = storeChats.filter(chat => chat && chat.id);
    
    // Sort: pinned first, then by last message time
    storeChats.sort((a, b) => {
      if (!a.id || !b.id) return 0; // Skip sorting if no ID
      
      const aPinned = pinnedChats.includes(a.id);
      const bPinned = pinnedChats.includes(b.id);
      
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;
      
      const dateA = a.lastMessageAt?.toDate?.() || new Date(a.lastMessageAt || 0);
      const dateB = b.lastMessageAt?.toDate?.() || new Date(b.lastMessageAt || 0);
      return dateB - dateA;
    });

    // Apply filter
    if (chatFilter === 'replied') {
      return storeChats.filter(chat => chat.id && hasReplied(chat.id));
    } else if (chatFilter === 'unreplied') {
      return storeChats.filter(chat => chat.id && !hasReplied(chat.id));
    }
    
    return storeChats;
  };

  const [auctions, setAuctions] = useState([]);
  const [buyerRequests, setBuyerRequests] = useState([]);
  const [showCreateBuyerRequestForm, setShowCreateBuyerRequestForm] = useState(false);
  const [editingTrackingOrderId, setEditingTrackingOrderId] = useState(null);
  const [editingTrackingNumber, setEditingTrackingNumber] = useState('');
  const [editingCourierCompany, setEditingCourierCompany] = useState(null);
  const [editingCustomCourierCompany, setEditingCustomCourierCompany] = useState(null);
  const [buyerRequestForm, setBuyerRequestForm] = useState({
    productName: '',
    description: '',
    category: '',
    quantity: '',
    unit: 'กรัม',
    desiredPrice: '',
    duration: 7
  });

  const fetchStore = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      // ดึงข้อมูลร้านจาก users collection
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();
      
      if (userData?.storeId) {
        const storeRef = doc(db, 'stores', userData.storeId);
        const storeSnap = await getDoc(storeRef);
        
        if (storeSnap.exists()) {
          const storeData = { id: storeSnap.id, ...storeSnap.data() };
          setStore(storeData);
          
          // Debug: Check ownerId matching
          console.log('🏪 StoreManagement: Store loaded:', {
            storeId: storeSnap.id,
            storeOwnerId: storeData.ownerId,
            currentUserUid: currentUser.uid,
            matches: storeData.ownerId === currentUser.uid ? '✅ MATCH' : '❌ NO MATCH'
          });
          
          // Get package subscription
          try {
            const subscription = await getStoreSubscription(storeSnap.id);
            setPackageType(subscription?.packageType || 'free');
          } catch (error) {
            console.error('Error fetching subscription:', error);
            setPackageType('free');
          }
          
          // ดึงสินค้า
          const productsQuery = query(
            collection(db, 'products'),
            where('storeId', '==', storeSnap.id)
          );
          const productsSnapshot = await getDocs(productsQuery);
          setProducts(productsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })));
          
          // ดึงออเดอร์
          const ordersQuery = query(
            collection(db, 'orders'),
            where('storeId', '==', storeSnap.id)
          );
          const ordersSnapshot = await getDocs(ordersQuery);
          setOrders(ordersSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })));
          
          // ดึงประมูล
          const auctionsQuery = query(
            collection(db, 'auctions'),
            where('sellerId', '==', currentUser.uid)
          );
          const auctionsSnapshot = await getDocs(auctionsQuery);
          setAuctions(auctionsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })));
          
          // ดึงประกาศหาสินค้า (ที่ผู้ใช้เป็น buyer)
          try {
            const buyerRequestsQuery = query(
              collection(db, 'buyerRequests'),
              where('buyerId', '==', currentUser.uid),
              orderBy('createdAt', 'desc')
            );
            const buyerRequestsSnapshot = await getDocs(buyerRequestsQuery);
            setBuyerRequests(buyerRequestsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            })));
          } catch (indexError) {
            // Fallback query without orderBy
            console.warn('Index not found for buyerRequests, using fallback query:', indexError);
            const fallbackQuery = query(
              collection(db, 'buyerRequests'),
              where('buyerId', '==', currentUser.uid)
            );
            const fallbackSnapshot = await getDocs(fallbackQuery);
            const requestsData = fallbackSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            // Sort manually
            requestsData.sort((a, b) => {
              const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
              const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
              return bTime - aTime;
            });
            setBuyerRequests(requestsData);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching store:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBuyerRequest = async (e) => {
    e.preventDefault();

    const feeCheck = checkCoinRequirement(coins, 'CREATE_BUYER_REQUEST');
    if (!feeCheck.hasEnough) {
      alert(feeCheck.message);
      return;
    }

    try {
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + parseInt(buyerRequestForm.duration));

      await createBuyerRequest({
        productName: buyerRequestForm.productName,
        description: buyerRequestForm.description,
        category: buyerRequestForm.category,
        quantity: parseFloat(buyerRequestForm.quantity),
        unit: buyerRequestForm.unit,
        desiredPrice: buyerRequestForm.desiredPrice ? parseFloat(buyerRequestForm.desiredPrice) : null,
        duration: parseInt(buyerRequestForm.duration),
        endDate: endDate
      });

      alert(t('storeManagement.createBuyerRequestSuccess'));
      setShowCreateBuyerRequestForm(false);
      setBuyerRequestForm({
        productName: '',
        description: '',
        category: '',
        quantity: '',
        unit: 'กรัม',
        desiredPrice: '',
        duration: 7
      });
      
      // Refresh buyer requests list
      fetchStore();
    } catch (error) {
      console.error('Error creating buyer request:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleCreateAuction = async (e) => {
    e.preventDefault();

    if (!selectedProductForAuction) {
      alert(t('storeManagement.pleaseSelectProduct'));
      return;
    }

    const feeCheck = checkCoinRequirement(coins, 'CREATE_AUCTION');
    if (!feeCheck.hasEnough) {
      alert(feeCheck.message);
      return;
    }

    try {
      const endTime = new Date();
      endTime.setHours(endTime.getHours() + parseInt(auctionForm.duration));

      await createAuction({
        productId: selectedProductForAuction.id,
        productName: selectedProductForAuction.name,
        storeId: selectedProductForAuction.storeId || store.id,
        startingPrice: parseFloat(auctionForm.startingPrice),
        reservePrice: auctionForm.reservePrice ? parseFloat(auctionForm.reservePrice) : null,
        auctionType: auctionForm.auctionType,
        duration: parseInt(auctionForm.duration),
        endTime: endTime
      });

      alert(t('storeManagement.createAuctionSuccess'));
      setShowCreateAuctionForm(false);
      setSelectedProductForAuction(null);
      setAuctionForm({
        startingPrice: '',
        reservePrice: '',
        auctionType: 'highest_bid',
        duration: 24
      });
      
      // Refresh auctions list
      fetchStore();
    } catch (error) {
      console.error('Error creating auction:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleOpenStore = async (e) => {
    e.preventDefault();
    
    // ตรวจสอบว่าเป็น Marketplace user หรือไม่
    if (!isMarketplaceUser(currentUser)) {
      alert('คุณไม่มีสิทธิ์เข้าถึง Marketplace');
      return;
    }

    // ตรวจสอบว่ามีร้านอยู่แล้วหรือไม่ (ควรมีร้านอัตโนมัติแล้ว)
    if (currentUser?.storeId) {
      alert('คุณมีร้านอยู่แล้ว กรุณารีเฟรชหน้าเว็บ');
      fetchStore();
      return;
    }

    const feeCheck = checkCoinRequirement(coins, 'OPEN_STORE');
    if (!feeCheck.hasEnough) {
      alert(feeCheck.message);
      return;
    }

    try {
      const storeId = await openStore(storeForm);
      alert(t('storeManagement.openStoreSuccess'));
      setShowOpenStoreForm(false);
      fetchStore();
    } catch (error) {
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    if (!store) {
      alert(t('storeManagement.pleaseOpenStore'));
      return;
    }

    // Validation: ถ้าเลือก bank_transfer ต้องเลือกบัญชีอย่างน้อย 1 บัญชี
    if (productForm.paymentMethods?.includes('bank_transfer')) {
      if (!productForm.selectedBankAccounts || productForm.selectedBankAccounts.length === 0) {
        alert('กรุณาเลือกบัญชีธนาคารอย่างน้อย 1 บัญชี');
        return;
      }
    }

    // Validation: ถ้าเลือก qr_code ต้องเลือก QR Code อย่างน้อย 1 ตัว
    if (productForm.paymentMethods?.includes('qr_code')) {
      if (!productForm.selectedQRCodes || productForm.selectedQRCodes.length === 0) {
        alert('กรุณาเลือก QR Code อย่างน้อย 1 ตัว');
        return;
      }
    }

    const feeCheck = checkCoinRequirement(coins, 'LIST_PRODUCT');
    if (!feeCheck.hasEnough) {
      alert(feeCheck.message);
      return;
    }

    try {
      // Filter out undefined values before sending to Firestore
      const productData = {
        name: productForm.name || '',
        description: productForm.description || '',
        price: parseFloat(productForm.price) || 0,
        quantity: parseInt(productForm.quantity) || 0,
        unit: productForm.unit || 'กรัม',
        category: productForm.category || '',
        strain: productForm.strain || '',
        type: productForm.type || '',
        images: productForm.images?.map(img => typeof img === 'string' ? img : img.url).filter(Boolean) || [],
        storeName: store.name,
        minimumOrderQuantity: productForm.minimumOrderQuantity ? parseFloat(productForm.minimumOrderQuantity) : null,
        // เพิ่มข้อมูลการชำระเงินและการจัดส่งที่เลือก
        paymentMethods: productForm.paymentMethods || [],
        shippingMethods: productForm.shippingMethods || [],
        // บัญชีธนาคารและ QR Code ที่เลือก (เก็บเป็น index)
        selectedBankAccounts: productForm.selectedBankAccounts || [],
        selectedQRCodes: productForm.selectedQRCodes || []
      };
      
      // Remove any undefined values
      Object.keys(productData).forEach(key => {
        if (productData[key] === undefined) {
          delete productData[key];
        }
      });
      
      await listProduct(productData, store.id);
      
      alert(t('storeManagement.listProductSuccess'));
      setShowAddProductForm(false);
      setProductForm({
        name: '',
        description: '',
        price: '',
        quantity: '',
        unit: 'กรัม',
        category: '',
        strain: '',
        type: '',
        images: [],
        minimumOrderQuantity: '',
        paymentMethods: [],
        shippingMethods: [],
        selectedBankAccounts: [],
        selectedQRCodes: []
      });
      fetchStore();
    } catch (error) {
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus, additionalData = {}) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        status: newStatus,
        ...additionalData,
        updatedAt: new Date()
      });
      alert(t('storeManagement.updateOrderStatusSuccess'));
      fetchStore();
    } catch (error) {
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (!store) {
    // ตรวจสอบว่าเป็น Marketplace user หรือไม่
    if (!isMarketplaceUser(currentUser)) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-8">
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                คุณไม่มีสิทธิ์เข้าถึง Marketplace กรุณาเข้าสู่ระบบด้วยบัญชี Marketplace (USER หรือ FARM)
              </div>
            </div>
          </div>
        </div>
      );
    }

    // ถ้าเป็น Marketplace user แต่ยังไม่มีร้าน (ควรไม่เกิดขึ้นเพราะสร้างอัตโนมัติ)
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">กำลังโหลดร้านของคุณ...</h2>
            <p className="text-gray-600 mb-6">
              {t('storeManagement.systemCreatingStore')}
            </p>
            
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
              💡 หมายเหตุ: ระบบควรสร้างร้านให้คุณอัตโนมัติแล้ว หากยังไม่เห็นร้าน กรุณารีเฟรชหน้าเว็บหรือติดต่อผู้ดูแลระบบ
              </div>

            <form onSubmit={showOpenStoreForm ? handleOpenStore : (e) => { e.preventDefault(); setShowOpenStoreForm(true); }}>
              {showOpenStoreForm ? (
                <>
                  <div className="mb-4">
                    <label className="block text-gray-700 font-semibold mb-2">ชื่อร้าน *</label>
                    <input
                      type="text"
                      required
                      value={storeForm.name}
                      onChange={(e) => setStoreForm({ ...storeForm, name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 font-semibold mb-2">คำอธิบาย</label>
                    <textarea
                      value={storeForm.description}
                      onChange={(e) => setStoreForm({ ...storeForm, description: e.target.value })}
                      rows="4"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 font-semibold mb-2">หมวดหมู่</label>
                    <select
                      value={storeForm.category}
                      onChange={(e) => setStoreForm({ ...storeForm, category: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">เลือกหมวดหมู่</option>
                      <option value="Flowers">Flowers</option>
                      <option value="Seeds">Seeds</option>
                      <option value="Edibles">Edibles</option>
                      <option value="Pre Rolls">Pre Rolls</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 font-semibold mb-2">เบอร์โทร</label>
                    <input
                      type="tel"
                      value={storeForm.contact.phone}
                      onChange={(e) => setStoreForm({
                        ...storeForm,
                        contact: { ...storeForm.contact, phone: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 font-semibold mb-2">Email</label>
                    <input
                      type="email"
                      value={storeForm.contact.email}
                      onChange={(e) => setStoreForm({
                        ...storeForm,
                        contact: { ...storeForm.contact, email: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 font-semibold mb-2">ที่อยู่</label>
                    <textarea
                      value={storeForm.contact.address}
                      onChange={(e) => setStoreForm({
                        ...storeForm,
                        contact: { ...storeForm.contact, address: e.target.value }
                      })}
                      rows="3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="submit"
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                    >
                      {t('storeManagement.openStore')} ({MARKETPLACE_FEES.OPEN_STORE.default} {t('common.coins')})
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowOpenStoreForm(false)}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                    >
                      ยกเลิก
                    </button>
                  </div>
                </>
              ) : (
                <button
                  type="submit"
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                >
                  {t('storeManagement.openStoreNew')}
                </button>
              )}
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 md:py-8 pb-20 md:pb-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-4 md:mb-6">
          {/* Cover Image */}
          <div className="relative h-32 md:h-48 bg-gradient-to-br from-green-400 to-green-600">
            {store.coverImage ? (
              <img
                src={store.coverImage}
                alt={store.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white text-4xl md:text-6xl">
                🏪
              </div>
            )}
            
            {/* Logo */}
            {store.logo && (
              <div className="absolute bottom-0 left-4 md:left-6 transform translate-y-1/2">
                <img
                  src={store.logo}
                  alt={store.name}
                  className="w-16 h-16 md:w-24 md:h-24 rounded-full border-4 border-white object-cover shadow-lg"
                />
              </div>
            )}
          </div>
          
          {/* Store Info */}
          <div className="p-4 md:p-6 pt-8 md:pt-12">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">{store.name}</h1>
                {store.reputationLevel && (
                  <LevelBadge level={store.reputationLevel} score={store.reputationScore} type="store" />
                )}
              </div>
              <div className="flex gap-4 md:text-right">
                <div className="flex-1 md:flex-none">
                  <p className="text-sm md:text-base text-gray-600">ออเดอร์: <span className="font-semibold">{orders.length}</span></p>
                  <p className="text-sm md:text-base text-gray-600">สินค้า: <span className="font-semibold">{products.length}</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-4 md:mb-6 overflow-hidden">
          <div className="flex border-b overflow-x-auto scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
            <button
              onClick={() => setActiveTab('products')}
              className={`px-4 md:px-6 py-3 font-semibold whitespace-nowrap flex-shrink-0 ${
                activeTab === 'products'
                  ? 'border-b-2 border-green-500 text-green-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              สินค้า ({products.length})
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className={`px-4 md:px-6 py-3 font-semibold whitespace-nowrap flex-shrink-0 ${
                activeTab === 'orders'
                  ? 'border-b-2 border-green-500 text-green-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {t('storeManagement.tabs.orders')} ({orders.length})
            </button>
            <button
              onClick={() => setActiveTab('chat')}
              className={`px-4 md:px-6 py-3 font-semibold whitespace-nowrap flex-shrink-0 ${
                activeTab === 'chat'
                  ? 'border-b-2 border-green-500 text-green-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              💬 {t('storeManagement.tabs.chats')} ({getStoreChats().length})
            </button>
            <button
              onClick={() => setActiveTab('auctions')}
              className={`px-4 md:px-6 py-3 font-semibold whitespace-nowrap flex-shrink-0 ${
                activeTab === 'auctions'
                  ? 'border-b-2 border-green-500 text-green-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              🎯 {t('storeManagement.tabs.auctions')} ({auctions.length})
            </button>
            <button
              onClick={() => setActiveTab('buyerRequests')}
              className={`px-4 md:px-6 py-3 font-semibold whitespace-nowrap flex-shrink-0 ${
                activeTab === 'buyerRequests'
                  ? 'border-b-2 border-green-500 text-green-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              📢 {t('storeManagement.tabs.buyerRequests')} ({buyerRequests.length})
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-4 md:px-6 py-3 font-semibold whitespace-nowrap flex-shrink-0 ${
                activeTab === 'settings'
                  ? 'border-b-2 border-green-500 text-green-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              ตั้งค่า
            </button>
          </div>
        </div>

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div>
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">{t('storeManagement.tabs.products')}</h2>
              {/* Desktop: Full Button */}
              <button
                onClick={() => setShowAddProductForm(true)}
                className="hidden md:flex bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors items-center gap-2"
              >
                <span className="text-xl">+</span>
                <span>{t('storeManagement.addProduct')} ({MARKETPLACE_FEES.LIST_PRODUCT.default} {t('common.coins')})</span>
              </button>
              {/* Mobile: Compact Button */}
              <button
                onClick={() => setShowAddProductForm(true)}
                className="md:hidden bg-green-500 hover:bg-green-600 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <span className="text-xl">+</span>
                <span className="text-sm">เพิ่มสินค้า</span>
              </button>
            </div>

            {showAddProductForm && (
              <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-4 md:mb-6">
                <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4">{t('storeManagement.addNewProduct')}</h3>
                <form onSubmit={handleAddProduct}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">{t('storeManagement.productName')} *</label>
                      <input
                        type="text"
                        required
                        value={productForm.name}
                        onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">{t('storeManagement.price')} *</label>
                      <input
                        type="number"
                        required
                        min="0"
                        step="0.01"
                        value={productForm.price}
                        onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">{t('storeManagement.quantity')} *</label>
                      <input
                        type="number"
                        required
                        min="0"
                        value={productForm.quantity}
                        onChange={(e) => setProductForm({ ...productForm, quantity: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">{t('storeManagement.unit')}</label>
                      <select
                        value={productForm.unit}
                        onChange={(e) => setProductForm({ ...productForm, unit: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="กรัม">กรัม</option>
                        <option value="กิโลกรัม">กิโลกรัม</option>
                        <option value="ต้น">ต้น</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">สั่งซื้อขั้นต่ำ (ไม่บังคับ)</label>
                      <input
                        type="number"
                        min="0"
                        value={productForm.minimumOrderQuantity}
                        onChange={(e) => setProductForm({ ...productForm, minimumOrderQuantity: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="เช่น 100"
                      />
                      <p className="text-xs text-gray-500 mt-1">ถ้าไม่ระบุ จะไม่มีข้อจำกัดการสั่งซื้อขั้นต่ำ</p>
                    </div>

                    {/* Category Selection - Icon Style */}
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">หมวดหมู่</label>
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { id: 'Flowers', name: 'Flowers', icon: '🌸' },
                          { id: 'Seeds', name: 'Seeds', icon: '🌱' },
                          { id: 'Edibles', name: 'Edibles', icon: '🍪' },
                          { id: 'Pre Rolls', name: 'Pre Rolls', icon: '🚬' },
                          { id: 'Cannabis Oils', name: 'Oils', icon: '💧' },
                          { id: 'CBD Products', name: 'CBD', icon: '💊' }
                        ].map(category => (
                          <button
                            key={category.id}
                            type="button"
                            onClick={() => setProductForm({ ...productForm, category: category.id })}
                            className={`flex flex-col items-center justify-center p-3 border-2 rounded-lg transition-all ${
                              productForm.category === category.id
                                ? 'border-green-500 bg-green-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <span className="text-2xl mb-1">{category.icon}</span>
                            <span className="text-xs font-medium text-gray-700">{category.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Type Selection - Icon Style */}
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">ประเภท</label>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { id: 'Sativa', name: 'Sativa', icon: '☀️' },
                          { id: 'Indica', name: 'Indica', icon: '🌙' },
                          { id: 'Hybrid', name: 'Hybrid', icon: '⚡' }
                        ].map(type => (
                          <button
                            key={type.id}
                            type="button"
                            onClick={() => setProductForm({ ...productForm, type: type.id })}
                            className={`flex flex-col items-center justify-center p-4 border-2 rounded-lg transition-all ${
                              productForm.type === type.id
                                ? 'border-green-500 bg-green-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <span className="text-3xl mb-2">{type.icon}</span>
                            <span className="text-sm font-semibold text-gray-700">{type.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-gray-700 font-semibold mb-2">คำอธิบาย</label>
                    <textarea
                      value={productForm.description}
                      onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                      rows="4"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div className="mt-4">
                    <ProductImageUpload
                      images={productForm.images?.map(url => ({ url })) || []}
                      onChange={(newImages) => setProductForm({ ...productForm, images: newImages.map(img => img.url) })}
                      maxImages={5}
                    />
                  </div>

                  {/* ข้อมูลการชำระเงินและการจัดส่ง */}
                  {store && (store.payment || store.shipping) && (
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4">ข้อมูลการชำระเงินและการจัดส่ง</h4>
                      
                      {/* เลือกวิธีการชำระเงิน */}
                      {store.payment && (
                        <div className="mb-6">
                          <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <span>💳</span> วิธีการชำระเงิน:
                          </h5>
                          <div className="space-y-2">
                            {/* Bank Transfer */}
                            {store.payment.bankAccounts && store.payment.bankAccounts.length > 0 && (
                              <div className="border border-gray-200 rounded-lg p-3">
                                <label className="flex items-start gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={productForm.paymentMethods?.includes('bank_transfer') || false}
                                    onChange={(e) => {
                                      const methods = productForm.paymentMethods || [];
                                      if (e.target.checked) {
                                        // ถ้าเลือก bank_transfer ให้เลือกบัญชีทั้งหมดอัตโนมัติ
                                        const allAccounts = store.payment.bankAccounts.map((_, index) => index);
                                        setProductForm({ 
                                          ...productForm, 
                                          paymentMethods: [...methods, 'bank_transfer'],
                                          selectedBankAccounts: allAccounts
                                        });
                                      } else {
                                        setProductForm({ 
                                          ...productForm, 
                                          paymentMethods: methods.filter(m => m !== 'bank_transfer'),
                                          selectedBankAccounts: []
                                        });
                                      }
                                    }}
                                    className="mt-1"
                                  />
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-700">โอนเงินผ่านบัญชีธนาคาร</div>
                                    <div className="text-xs text-gray-500 ml-4">
                                      {store.payment.bankAccounts.length} บัญชี
                                    </div>
                                  </div>
                                </label>
                                
                                {/* แสดงรายการบัญชีให้เลือก (ถ้าเลือก bank_transfer และมีบัญชีมากกว่า 1) */}
                                {productForm.paymentMethods?.includes('bank_transfer') && store.payment.bankAccounts.length > 1 && (
                                  <div className="mt-3 ml-6 space-y-2 border-t border-gray-200 pt-3">
                                    <div className="text-xs font-medium text-gray-600 mb-2">เลือกบัญชีที่ต้องการ:</div>
                                    {store.payment.bankAccounts.map((account, index) => (
                                      <label key={index} className="flex items-start gap-2 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={productForm.selectedBankAccounts?.includes(index) || false}
                                          onChange={(e) => {
                                            const selected = productForm.selectedBankAccounts || [];
                                            let newSelected;
                                            if (e.target.checked) {
                                              newSelected = [...selected, index];
                                            } else {
                                              newSelected = selected.filter(i => i !== index);
                                              // ถ้ายกเลิกการเลือกบัญชีทั้งหมด ให้ยกเลิก bank_transfer ด้วย
                                              if (newSelected.length === 0) {
                                                const methods = productForm.paymentMethods || [];
                                                setProductForm({ 
                                                  ...productForm, 
                                                  paymentMethods: methods.filter(m => m !== 'bank_transfer'),
                                                  selectedBankAccounts: []
                                                });
                                                return;
                                              }
                                            }
                                            setProductForm({ 
                                              ...productForm, 
                                              selectedBankAccounts: newSelected
                                            });
                                          }}
                                          className="mt-1"
                                        />
                                        <div className="flex-1 text-sm text-gray-700">
                                          <div className="font-medium">
                                            {account.bankName} - {account.accountNumber}
                                          </div>
                                          {account.accountName && (
                                            <div className="text-xs text-gray-500">
                                              ชื่อบัญชี: {account.accountName}
                                            </div>
                                          )}
                                        </div>
                                      </label>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* QR Code */}
                            {store.payment.qrCodes && store.payment.qrCodes.length > 0 && (
                              <div className="border border-gray-200 rounded-lg p-3">
                                <label className="flex items-start gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={productForm.paymentMethods?.includes('qr_code') || false}
                                    onChange={(e) => {
                                      const methods = productForm.paymentMethods || [];
                                      if (e.target.checked) {
                                        // ถ้าเลือก qr_code ให้เลือก QR Code ทั้งหมดอัตโนมัติ
                                        const allQRCodes = store.payment.qrCodes.map((_, index) => index);
                                        setProductForm({ 
                                          ...productForm, 
                                          paymentMethods: [...methods, 'qr_code'],
                                          selectedQRCodes: allQRCodes
                                        });
                                      } else {
                                        setProductForm({ 
                                          ...productForm, 
                                          paymentMethods: methods.filter(m => m !== 'qr_code'),
                                          selectedQRCodes: []
                                        });
                                      }
                                    }}
                                    className="mt-1"
                                  />
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-700">QR Code (PromptPay)</div>
                                    <div className="text-xs text-gray-500 ml-4">
                                      {store.payment.qrCodes.length} QR Code
                                    </div>
                                  </div>
                                </label>
                                
                                {/* แสดงรายการ QR Code ให้เลือก (ถ้าเลือก qr_code และมี QR Code มากกว่า 1) */}
                                {productForm.paymentMethods?.includes('qr_code') && store.payment.qrCodes.length > 1 && (
                                  <div className="mt-3 ml-6 space-y-2 border-t border-gray-200 pt-3">
                                    <div className="text-xs font-medium text-gray-600 mb-2">เลือก QR Code ที่ต้องการ:</div>
                                    {store.payment.qrCodes.map((qrCode, index) => (
                                      <label key={index} className="flex items-start gap-2 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={productForm.selectedQRCodes?.includes(index) || false}
                                          onChange={(e) => {
                                            const selected = productForm.selectedQRCodes || [];
                                            let newSelected;
                                            if (e.target.checked) {
                                              newSelected = [...selected, index];
                                            } else {
                                              newSelected = selected.filter(i => i !== index);
                                              // ถ้ายกเลิกการเลือก QR Code ทั้งหมด ให้ยกเลิก qr_code ด้วย
                                              if (newSelected.length === 0) {
                                                const methods = productForm.paymentMethods || [];
                                                setProductForm({ 
                                                  ...productForm, 
                                                  paymentMethods: methods.filter(m => m !== 'qr_code'),
                                                  selectedQRCodes: []
                                                });
                                                return;
                                              }
                                            }
                                            setProductForm({ 
                                              ...productForm, 
                                              selectedQRCodes: newSelected
                                            });
                                          }}
                                          className="mt-1"
                                        />
                                        <div className="flex-1 text-sm text-gray-700">
                                          <div className="font-medium">
                                            QR Code #{index + 1}
                                            {qrCode.amount && (
                                              <span className="text-xs text-gray-500 ml-2">
                                                (สำหรับยอด {qrCode.amount.toLocaleString('th-TH')} บาท)
                                              </span>
                                            )}
                                          </div>
                                          {qrCode.phoneNumber && (
                                            <div className="text-xs text-gray-500">
                                              เบอร์: {qrCode.phoneNumber}
                                            </div>
                                          )}
                                        </div>
                                      </label>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Cash on Pickup */}
                            {store.payment.cashOnPickup && (
                              <label className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-100 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={productForm.paymentMethods?.includes('cash_on_pickup') || false}
                                  onChange={(e) => {
                                    const methods = productForm.paymentMethods || [];
                                    if (e.target.checked) {
                                      setProductForm({ ...productForm, paymentMethods: [...methods, 'cash_on_pickup'] });
                                    } else {
                                      setProductForm({ ...productForm, paymentMethods: methods.filter(m => m !== 'cash_on_pickup') });
                                    }
                                  }}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <div className="font-medium text-gray-700">จ่ายสด นัดรับ</div>
                                </div>
                              </label>
                            )}
                            
                            {/* Cash on Delivery */}
                            {store.payment.cashOnDelivery && (
                              <label className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-100 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={productForm.paymentMethods?.includes('cash_on_delivery') || false}
                                  onChange={(e) => {
                                    const methods = productForm.paymentMethods || [];
                                    if (e.target.checked) {
                                      setProductForm({ ...productForm, paymentMethods: [...methods, 'cash_on_delivery'] });
                                    } else {
                                      setProductForm({ ...productForm, paymentMethods: methods.filter(m => m !== 'cash_on_delivery') });
                                    }
                                  }}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <div className="font-medium text-gray-700">เก็บปลายทาง (COD)</div>
                                </div>
                              </label>
                            )}
                          </div>
                        </div>
                      )}

                      {/* เลือกวิธีการจัดส่ง */}
                      {store.shipping && store.shipping.methods && store.shipping.methods.length > 0 && (
                        <div>
                          <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <span>🚚</span> วิธีการจัดส่ง:
                          </h5>
                          <div className="space-y-2">
                            {/* Courier */}
                            {store.shipping.methods.includes('courier') && (
                              <label className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-100 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={productForm.shippingMethods?.includes('courier') || false}
                                  onChange={(e) => {
                                    const methods = productForm.shippingMethods || [];
                                    if (e.target.checked) {
                                      setProductForm({ ...productForm, shippingMethods: [...methods, 'courier'] });
                                    } else {
                                      setProductForm({ ...productForm, shippingMethods: methods.filter(m => m !== 'courier') });
                                    }
                                  }}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <div className="font-medium text-gray-700">บริษัทขนส่งเอกชน</div>
                                  {store.shipping.courierCompanies && store.shipping.courierCompanies.length > 0 ? (
                                    <div className="text-xs text-gray-500 ml-4">
                                      {store.shipping.courierCompanies.join(', ')}
                                    </div>
                                  ) : (
                                    <div className="text-xs text-gray-400 ml-4">ยังไม่ได้เลือกบริษัทขนส่ง</div>
                                  )}
                                  {store.shipping.fees?.courier && (
                                    <div className="text-xs text-gray-500 ml-4">
                                      ค่าจัดส่ง: {store.shipping.fees.courier.type === 'fixed' 
                                        ? `฿${store.shipping.fees.courier.amount.toLocaleString('th-TH')}` 
                                        : 'ตามน้ำหนัก/ระยะทาง'}
                                    </div>
                                  )}
                                </div>
                              </label>
                            )}
                            
                            {/* Pickup */}
                            {store.shipping.methods.includes('pickup') && (
                              <label className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-100 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={productForm.shippingMethods?.includes('pickup') || false}
                                  onChange={(e) => {
                                    const methods = productForm.shippingMethods || [];
                                    if (e.target.checked) {
                                      setProductForm({ ...productForm, shippingMethods: [...methods, 'pickup'] });
                                    } else {
                                      setProductForm({ ...productForm, shippingMethods: methods.filter(m => m !== 'pickup') });
                                    }
                                  }}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <div className="font-medium text-gray-700">นัดรับ</div>
                                  {store.shipping.pickupLocation && (
                                    <div className="text-xs text-gray-500 ml-4">ที่อยู่: {store.shipping.pickupLocation}</div>
                                  )}
                                  {store.shipping.pickupTime && (
                                    <div className="text-xs text-gray-500 ml-4">เวลา: {store.shipping.pickupTime}</div>
                                  )}
                                  <div className="text-xs text-green-600 font-medium ml-4">ฟรี</div>
                                </div>
                              </label>
                            )}
                            
                            {/* Special Vehicle */}
                            {store.shipping.methods.includes('special_vehicle') && (
                              <label className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-100 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={productForm.shippingMethods?.includes('special_vehicle') || false}
                                  onChange={(e) => {
                                    const methods = productForm.shippingMethods || [];
                                    if (e.target.checked) {
                                      setProductForm({ ...productForm, shippingMethods: [...methods, 'special_vehicle'] });
                                    } else {
                                      setProductForm({ ...productForm, shippingMethods: methods.filter(m => m !== 'special_vehicle') });
                                    }
                                  }}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <div className="font-medium text-gray-700">ฝากส่งกับรถพิเศษ</div>
                                  {store.shipping.specialVehicleInfo && (
                                    <div className="text-xs text-gray-500 ml-4">{store.shipping.specialVehicleInfo}</div>
                                  )}
                                  {store.shipping.fees?.specialVehicle && (
                                    <div className="text-xs text-gray-500 ml-4">
                                      ค่าจัดส่ง: {store.shipping.fees.specialVehicle.type === 'fixed' 
                                        ? `฿${store.shipping.fees.specialVehicle.amount.toLocaleString('th-TH')}` 
                                        : 'ตามน้ำหนัก/ระยะทาง'}
                                    </div>
                                  )}
                                </div>
                              </label>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-4 mt-6">
                    <button
                      type="submit"
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                    >
                      เพิ่มสินค้า ({MARKETPLACE_FEES.LIST_PRODUCT.default} เหรียญ)
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddProductForm(false)}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                    >
                      ยกเลิก
                    </button>
                  </div>
                </form>
              </div>
            )}

            {products.length > 0 ? (
              <>
                {/* Desktop: Grid View */}
                <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                  {products.map(product => (
                    <ProductCard key={product.id} product={product} showAddToCart={false} />
                  ))}
                </div>
                {/* Mobile: List View */}
                <div className="md:hidden space-y-3">
                  {products.map(product => (
                    <ProductListCard key={product.id} product={product} showAddToCart={false} />
                  ))}
                </div>
              </>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-8 md:p-12 text-center">
                <p className="text-gray-500 text-sm md:text-base">{t('storeManagement.noProducts')}</p>
              </div>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div>
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">{t('storeManagement.myOrders')}</h2>
              {selectedOrdersForPackingSlip.length > 0 && (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">
                    เลือกแล้ว: {selectedOrdersForPackingSlip.length} ออเดอร์
                  </span>
                  <button
                    onClick={() => {
                      const selectedOrders = orders.filter(o => 
                        selectedOrdersForPackingSlip.includes(o.id) && 
                        (o.status === 'paid' || o.status === 'shipping')
                      );
                      if (selectedOrders.length > 0) {
                        setSelectedOrderForPrint(null); // Clear single order selection
                        setShowPackingSlipModal(true);
                      } else {
                        alert('กรุณาเลือกออเดอร์ที่มีสถานะ "ชำระเงินแล้ว" หรือ "กำลังจัดส่ง"');
                      }
                    }}
                    className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
                  >
                    📋 พิมพ์ใบปะหน้า ({selectedOrdersForPackingSlip.length} ออเดอร์)
                  </button>
                  <button
                    onClick={() => setSelectedOrdersForPackingSlip([])}
                    className="text-sm text-gray-600 hover:text-gray-800 underline"
                  >
                    ยกเลิก
                  </button>
                </div>
              )}
            </div>
            {orders.length > 0 ? (
              <div className="space-y-4 md:space-y-5">
                {orders.map(order => {
                  const isSelected = selectedOrdersForPackingSlip.includes(order.id);
                  const canSelectForPackingSlip = order.status === 'paid' || order.status === 'shipping';
                  
                  return (
                  <div 
                    key={order.id} 
                    className={`group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-gray-200 transform hover:-translate-y-1 ${isSelected ? 'ring-2 ring-orange-500 border-2 border-orange-500' : ''}`}
                  >
                    {/* Order Header with Gradient */}
                    <div className={`p-5 md:p-6 bg-gradient-to-r from-green-50 via-green-50 to-white`}>
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-start gap-3 flex-1">
                          {canSelectForPackingSlip && (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedOrdersForPackingSlip([...selectedOrdersForPackingSlip, order.id]);
                                } else {
                                  setSelectedOrdersForPackingSlip(selectedOrdersForPackingSlip.filter(id => id !== order.id));
                                }
                              }}
                              className="mt-1 w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500 z-10"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            {/* Order ID */}
                            <div className="flex items-center gap-3 mb-3 flex-wrap">
                              <h3 className="text-lg md:text-xl font-bold text-gray-900">
                                {t('orders.orderId')} <span className="text-gray-600">#{order.orderNumber}</span>
                              </h3>
                              {/* Order Type Badge - Sell Order */}
                              <span className="px-3 py-1.5 rounded-full text-xs font-bold shadow-sm bg-gradient-to-r from-green-500 to-green-600 text-white">
                                🏪 {t('orders.sellOrder')}
                              </span>
                            </div>
                            
                            {/* Order Info Cards */}
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <span className="text-gray-400">👤</span>
                                <span className="font-medium">{t('orders.buyer')}:</span>
                                <span className="font-semibold text-gray-900">{order.buyerName}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <span className="text-gray-400">📅</span>
                                <span className="font-medium">{t('orders.date')}:</span>
                                <span className="text-gray-900">{new Date(order.createdAt?.toDate?.() || order.createdAt).toLocaleDateString('th-TH')}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Right Side - Status and Amount */}
                        <div className="text-right ml-4 flex-shrink-0">
                          {/* Enhanced Status Badge */}
                          <div className={`px-3 py-1.5 rounded-xl text-xs md:text-sm font-bold mb-3 shadow-sm ${
                            order.status === 'pending_payment' ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white' :
                            order.status === 'paid' ? 'bg-gradient-to-r from-blue-400 to-blue-500 text-white' :
                            order.status === 'shipping' ? 'bg-gradient-to-r from-purple-400 to-purple-500 text-white' :
                            order.status === 'delivered' ? 'bg-gradient-to-r from-green-400 to-green-500 text-white' :
                            'bg-gradient-to-r from-gray-400 to-gray-500 text-white'
                          }`}>
                            {order.status === 'pending_payment' ? '⏳ ' + t('orders.pendingPayment') :
                             order.status === 'paid' ? '💳 ' + t('orders.paid') :
                             order.status === 'shipping' ? '🚚 ' + t('orders.shipping') :
                             order.status === 'delivered' ? '✅ ' + t('orders.delivered') :
                             order.status}
                          </div>
                          {/* Enhanced Amount Display */}
                          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-3 border border-green-200 mb-3">
                            <p className="text-xs text-gray-600 mb-1 font-medium">{t('orders.total')}</p>
                            <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent">
                              ฿{order.total?.toLocaleString()}
                            </p>
                            {order.fee > 0 && (
                              <p className="text-[10px] text-gray-500 mt-1">
                                ({t('orders.fee') || 'ค่าธรรมเนียม'}: {order.fee} {t('common.coins') || 'เหรียญ'})
                              </p>
                            )}
                          </div>
                          {/* Product Image with Name Overlay */}
                          {order.items && order.items.length > 0 && order.items[0].image && (
                            <div className="flex flex-col items-end">
                              <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border-2 border-gray-200 shadow-sm">
                                <div className="relative w-full h-full">
                                  <img
                                    src={order.items[0].image}
                                    alt={order.items[0].productName || order.items[0].name || 'Product'}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                  {/* Gradient overlay for text readability */}
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                                  {/* Subtle border overlay */}
                                  <div className="absolute inset-0 border border-gray-200/30 rounded-lg"></div>
                                </div>
                                {/* Product Name Overlay */}
                                <div className="absolute bottom-0 left-0 right-0 p-1.5 md:p-2">
                                  <p className="text-[10px] md:text-xs text-white font-semibold text-center line-clamp-2 leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                                    {order.items[0].productName || order.items[0].name || 'สินค้า'}
                                  </p>
                                </div>
                                {/* Show count if multiple items */}
                                {order.items.length > 1 && (
                                  <div className="absolute -top-1 -right-1 bg-gray-800/80 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center border border-white z-10">
                                    +{order.items.length - 1}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="px-5 md:px-6 pb-5 md:pb-6 space-y-4">
                      {/* Product List */}
                      {order.items && order.items.length > 0 && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                          <h4 className="font-semibold text-gray-700 mb-3 text-sm md:text-base">{t('orders.productList')}:</h4>
                          <div className="space-y-2">
                            {order.items.map((item, index) => (
                              <div key={index} className="flex justify-between items-center p-2 bg-white rounded-lg">
                                <span className="text-sm text-gray-800 font-medium">
                                  {item.productName || item.name} x {item.quantity}
                                </span>
                                <span className="text-sm font-semibold text-gray-900">
                                  ฿{item.subtotal?.toLocaleString()}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Payment Proof */}
                      {order.paymentProof && (
                        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                          <p className="font-semibold text-gray-700 mb-2 text-sm md:text-base">{t('orders.paymentProof') || 'หลักฐานการโอน'}:</p>
                          <PaymentProofViewer 
                            paymentProofUrl={order.paymentProof} 
                            alt="Payment Proof"
                            showLabel={true}
                            size="sm"
                          />
                        </div>
                      )}

                      {/* Tracking Number */}
                      {order.trackingNumber && (
                        <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                          <p className="font-semibold text-gray-700 mb-2 text-sm md:text-base">{t('orders.trackingNumber') || 'เลขพัสดุ'}:</p>
                          <div className="space-y-1">
                            <p className="text-gray-800 font-medium">{order.trackingNumber}</p>
                            {(order.courierCompany || order.customCourierCompany) && (
                              <p className="text-sm text-gray-600">
                                {t('orders.courierCompany') || 'บริษัทขนส่ง'}: {order.customCourierCompany || order.courierCompany}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2 flex-wrap pt-2">
                        {order.status === 'pending_payment' && order.paymentProof && (
                          <button
                            onClick={() => handleUpdateOrderStatus(order.id, 'paid', { paymentDate: new Date() })}
                            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
                          >
                            ✅ {t('orders.confirmPayment') || 'ยืนยันการชำระเงิน'}
                          </button>
                        )}
                        {(order.status === 'paid' || order.status === 'shipping') && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedOrderForPrint(order);
                                setShowPackingSlipModal(true);
                              }}
                              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg flex items-center gap-2"
                            >
                              📋 {t('orders.printPackingSlip') || 'พิมพ์ใบปะหน้า'}
                            </button>
                            <button
                              onClick={() => {
                                setSelectedOrderForPrint(order);
                                setShowShippingLabelModal(true);
                              }}
                              className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg flex items-center gap-2"
                            >
                              🖨️ {t('orders.printShippingLabel') || 'พิมพ์ใบส่งของ'}
                            </button>
                          </>
                        )}
                        {order.status === 'paid' && (
                          <>
                            {editingTrackingOrderId === order.id ? (
                              <div className="w-full p-4 bg-blue-50 rounded-xl border-2 border-blue-300 mb-2">
                                <h4 className="font-semibold text-gray-700 mb-3">📦 {t('orders.addTrackingNumber') || 'แปะเลขพัสดุ'}</h4>
                                <TrackingNumberInput
                                  value={editingTrackingNumber}
                                  courierCompany={editingCourierCompany}
                                  customCourierCompany={editingCustomCourierCompany}
                                  onChange={(newTrackingNumber, newCourierCompany, newCustomCourierCompany) => {
                                    setEditingTrackingNumber(newTrackingNumber);
                                    setEditingCourierCompany(newCourierCompany);
                                    setEditingCustomCourierCompany(newCustomCourierCompany);
                                  }}
                                  onSave={async (newTrackingNumber, newCourierCompany, newCustomCourierCompany) => {
                                    try {
                                      await handleUpdateOrderStatus(order.id, 'shipping', {
                                        trackingNumber: newTrackingNumber,
                                        courierCompany: newCourierCompany || null,
                                        customCourierCompany: newCustomCourierCompany || null,
                                        shippingDate: new Date()
                                      });
                                      setEditingTrackingOrderId(null);
                                      setEditingTrackingNumber('');
                                      setEditingCourierCompany(null);
                                      setEditingCustomCourierCompany(null);
                                    } catch (error) {
                                      alert('เกิดข้อผิดพลาด: ' + error.message);
                                    }
                                  }}
                                />
                                <button
                                  onClick={() => {
                                    setEditingTrackingOrderId(null);
                                    setEditingTrackingNumber('');
                                    setEditingCourierCompany(null);
                                    setEditingCustomCourierCompany(null);
                                  }}
                                  className="mt-2 text-sm text-gray-600 hover:text-gray-700 underline"
                                >
                                  {t('common.cancel') || 'ยกเลิก'}
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  setEditingTrackingOrderId(order.id);
                                  setEditingTrackingNumber(order.trackingNumber || '');
                                  setEditingCourierCompany(order.courierCompany || null);
                                  setEditingCustomCourierCompany(order.customCourierCompany || null);
                                }}
                                className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
                              >
                                📦 {t('orders.addTrackingNumber') || 'แปะเลขพัสดุ'}
                              </button>
                            )}
                          </>
                        )}
                        {order.status === 'shipping' && (
                          <button
                            onClick={() => handleUpdateOrderStatus(order.id, 'delivered', { deliveredDate: new Date() })}
                            className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
                          >
                            ✅ {t('orders.markAsDelivered') || 'ส่งแล้ว'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-lg p-12 md:p-16 text-center border border-gray-100">
                <div className="max-w-md mx-auto">
                  <div className="text-6xl mb-4">📦</div>
                  <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">{t('orders.noOrders')}</h3>
                  <p className="text-sm md:text-base text-gray-500">
                    {t('orders.noOrdersDescription') || 'ยังไม่มีออเดอร์ในระบบ'}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">แชทกับลูกค้า</h2>
            </div>

            {/* Sub Tabs */}
            <div className="flex gap-2 mb-4 md:mb-6 border-b border-gray-200 overflow-x-auto">
              <button
                onClick={() => setChatSubTab('chats')}
                className={`px-4 md:px-6 py-3 font-semibold border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                  chatSubTab === 'chats'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
              >
                💬 แชท
              </button>
              <button
                onClick={() => setChatSubTab('videoCallRequests')}
                className={`px-4 md:px-6 py-3 font-semibold border-b-2 transition-colors relative whitespace-nowrap flex-shrink-0 ${
                  chatSubTab === 'videoCallRequests'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
              >
                <div className="flex items-center gap-2">
                  <VideoCallIcon className="w-5 h-5" />
                  <span>{t('storeManagement.tabs.videoCallRequests')}</span>
                </div>
                {store && <VideoCallRequestBadge storeId={store.id} />}
              </button>
            </div>

            {/* Video Call Requests Sub Tab */}
            {chatSubTab === 'videoCallRequests' && store && (
              <div>
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    🔍 Debug: Store ID = {store.id}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    💡 ตรวจสอบ Console (F12) เพื่อดู debug logs
                  </p>
                </div>
                <VideoCallRequestsList storeId={store.id} />
              </div>
            )}

            {/* Chats Sub Tab */}
            {chatSubTab === 'chats' && (
              <>
            {/* Filter Buttons */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setChatFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  chatFilter === 'all'
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ทั้งหมด
              </button>
              <button
                onClick={() => setChatFilter('unreplied')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  chatFilter === 'unreplied'
                    ? 'bg-red-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ยังไม่ตอบ
              </button>
              <button
                onClick={() => setChatFilter('replied')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  chatFilter === 'replied'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ตอบแล้ว
              </button>
            </div>

            {/* Chat List */}
            {(() => {
              const filteredChats = getFilteredChats();
              console.log('Filtered chats count:', filteredChats.length);
              console.log('Store chats count:', getStoreChats().length);
              console.log('All chats count:', chats?.length || 0);
              
              if (filteredChats.length === 0) {
                return (
                  <div className="text-center py-12">
                    <div className="text-4xl mb-4">💬</div>
                    <p className="text-gray-600">ยังไม่มีแชท</p>
                    {getStoreChats().length > 0 && (
                      <p className="text-sm text-gray-500 mt-2">
                        (พบ {getStoreChats().length} แชท แต่ถูก filter ออก)
                      </p>
                    )}
                  </div>
                );
              }
              
              return (
                <div className="space-y-2">
                  {filteredChats.map((chat) => {
                    if (!chat || !chat.id) {
                      console.warn('Chat without ID in render:', chat);
                      return null;
                    }
                  
                  const otherUserId = chat.participants?.find(id => id !== currentUser?.uid);
                  const isPinned = pinnedChats.includes(chat.id);
                  const hasRepliedTo = hasReplied(chat.id);
                  const product = chat.type === 'product_inquiry' && chat.productId 
                    ? productCache[chat.productId] 
                    : null;
                  const productImage = product?.images?.[0] || product?.image || null;
                  
                  const handleChatClick = () => {
                    if (chat.id) {
                      navigate(`/marketplace/chat/${chat.id}`, { state: { fromStoreManagement: true } });
                    } else {
                      console.error('Cannot navigate: chat.id is missing', chat);
                      alert('เกิดข้อผิดพลาด: ไม่พบ ID ของแชท');
                    }
                  };
                  
                  return (
                    <div
                      key={chat.id}
                      className={`border rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                        isPinned ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200'
                      } ${!hasRepliedTo ? 'bg-blue-50 border-blue-200' : ''}`}
                      onClick={handleChatClick}
                    >
                      <div className="flex items-start justify-between gap-3">
                        {/* Product Image */}
                        {productImage && (
                          <div className="flex-shrink-0">
                            <img 
                              src={productImage} 
                              alt={product?.name || 'สินค้า'}
                              className="w-16 h-16 object-cover rounded-lg"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {isPinned && (
                              <span className="text-yellow-500" title="ปักหมุด">📌</span>
                            )}
                            <h3 className="font-semibold text-gray-800 truncate">
                              {chat.type === 'product_inquiry' && product ? (
                                <span className="text-sm">{product.name || 'สอบถามสินค้า'}</span>
                              ) : chat.type === 'product_inquiry' ? (
                                <span className="text-sm text-gray-500">สอบถามสินค้า</span>
                              ) : (
                                `ผู้ใช้ ${otherUserId?.substring(0, 8)}...`
                              )}
                            </h3>
                            {!hasRepliedTo && (
                              <span className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded-full">
                                ยังไม่ตอบ
                              </span>
                            )}
                          </div>
                          
                          {chat.type === 'product_inquiry' && product && (
                            <p className="text-xs text-gray-500 mb-2">
                              {product.price ? `฿${product.price.toLocaleString()}` : ''}
                            </p>
                          )}
                          
                          {chat.lastMessage && (
                            <p className="text-sm text-gray-600 truncate">
                              {chat.lastMessage}
                            </p>
                          )}
                          
                          {chat.lastMessageAt && (
                            <p className="text-xs text-gray-400 mt-1">
                              {(() => {
                                const date = chat.lastMessageAt?.toDate?.() || new Date(chat.lastMessageAt);
                                const now = new Date();
                                const diff = now - date;
                                const minutes = Math.floor(diff / 60000);
                                const hours = Math.floor(diff / 3600000);
                                const days = Math.floor(diff / 86400000);
                                
                                if (minutes < 1) return 'เมื่อสักครู่';
                                if (minutes < 60) return `${minutes} นาทีที่แล้ว`;
                                if (hours < 24) return `${hours} ชั่วโมงที่แล้ว`;
                                if (days < 7) return `${days} วันที่แล้ว`;
                                return date.toLocaleDateString('th-TH');
                              })()}
                            </p>
                          )}
                        </div>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePinChat(chat.id);
                          }}
                          className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                            isPinned
                              ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                          }`}
                          title={isPinned ? 'ยกเลิกปักหมุด' : 'ปักหมุด'}
                        >
                          📌
                        </button>
                      </div>
                    </div>
                  );
                  })}
                </div>
              );
              })()}
              </>
            )}
          </div>
        )}

        {/* Auctions Tab */}
        {activeTab === 'auctions' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">ประมูลของฉัน</h2>
              <button
                onClick={() => {
                  // Check if store has products
                  const availableProducts = products.filter(p => p.status === 'active' && !p.isAuction);
                  if (availableProducts.length === 0) {
                    alert('กรุณาสร้างสินค้าก่อนเปิดประมูล');
                    setActiveTab('products');
                    setShowAddProductForm(true);
                    return;
                  }
                  setShowCreateAuctionForm(true);
                }}
                className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
              >
                <span>+</span>
                <span>เปิดประมูลใหม่</span>
              </button>
            </div>

            {auctions.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">🎯</div>
                <p className="text-gray-600">ยังไม่มีประมูล</p>
              </div>
            ) : (
              <div className="space-y-4">
                {auctions.map((auction) => {
                  // Handle Firestore Timestamp or Date string for endTime
                  const getEndTime = () => {
                    if (!auction.endTime) return null;
                    
                    // If it's a Firestore Timestamp
                    if (auction.endTime.toDate && typeof auction.endTime.toDate === 'function') {
                      return auction.endTime.toDate();
                    }
                    
                    // If it's already a Date
                    if (auction.endTime instanceof Date) {
                      return auction.endTime;
                    }
                    
                    // If it's a string or number, try to parse it
                    const parsed = new Date(auction.endTime);
                    if (!isNaN(parsed.getTime())) {
                      return parsed;
                    }
                    
                    return null;
                  };

                  const endTime = getEndTime();
                  const now = new Date();
                  
                  // Check if auction is active: must have endTime, not ended, and status is active
                  const isActive = endTime && 
                    endTime > now && 
                    !auction.isEnded && 
                    auction.status === 'active';
                  
                  const timeLeft = endTime ? endTime - now : 0;
                  const hoursLeft = Math.max(0, Math.floor(timeLeft / 3600000));
                  const daysLeft = Math.floor(hoursLeft / 24);
                  
                  return (
                    <div
                      key={auction.id}
                      className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-gray-800">
                              {auction.productName || 'สินค้าประมูล'}
                            </h3>
                            {isActive ? (
                              <span className="px-2 py-1 bg-green-100 text-green-600 text-xs rounded-full">
                                กำลังประมูล
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                สิ้นสุดแล้ว
                              </span>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500">ราคาเริ่มต้น</p>
                              <p className="font-semibold text-gray-800">
                                ฿{auction.startingPrice?.toLocaleString() || 0}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500">ราคาปัจจุบัน</p>
                              <p className="font-semibold text-green-600">
                                ฿{auction.currentBid?.toLocaleString() || auction.startingPrice?.toLocaleString() || 0}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500">จำนวนผู้เสนอราคา</p>
                              <p className="font-semibold text-gray-800">
                                {auction.bids?.length || auction.bidCount || 0} คน
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500">เวลาที่เหลือ</p>
                              <p className="font-semibold text-gray-800">
                                {isActive && endTime ? (
                                  daysLeft > 0 ? `${daysLeft} วัน` : hoursLeft > 0 ? `${hoursLeft} ชั่วโมง` : 'น้อยกว่า 1 ชั่วโมง'
                                ) : (
                                  'สิ้นสุดแล้ว'
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <button
                          onClick={() => navigate(`/marketplace/auctions/${auction.id}`)}
                          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                        >
                          ดูรายละเอียด
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Create Auction Form Modal */}
        {showCreateAuctionForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800">เปิดประมูลใหม่</h2>
                  <button
                    onClick={() => {
                      setShowCreateAuctionForm(false);
                      setSelectedProductForAuction(null);
                      setAuctionForm({
                        startingPrice: '',
                        reservePrice: '',
                        auctionType: 'highest_bid',
                        duration: 24
                      });
                    }}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ✕
                  </button>
                </div>

                <form onSubmit={handleCreateAuction}>
                  <div className="space-y-4">
                    {/* Select Product */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        เลือกสินค้า *
                      </label>
                      <select
                        value={selectedProductForAuction?.id || ''}
                        onChange={(e) => {
                          const product = products.find(p => p.id === e.target.value);
                          setSelectedProductForAuction(product || null);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        required
                      >
                        <option value="">เลือกสินค้า</option>
                        {products
                          .filter(p => p.status === 'active' && !p.isAuction)
                          .map(product => (
                            <option key={product.id} value={product.id}>
                              {product.name} - ฿{product.price?.toLocaleString() || 0}
                            </option>
                          ))}
                      </select>
                    </div>

                    {/* Starting Price */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ราคาเริ่มต้น *
                      </label>
                      <input
                        type="number"
                        value={auctionForm.startingPrice}
                        onChange={(e) => setAuctionForm({ ...auctionForm, startingPrice: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="0"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>

                    {/* Reserve Price */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ราคาขั้นต่ำ (ไม่บังคับ)
                      </label>
                      <input
                        type="number"
                        value={auctionForm.reservePrice}
                        onChange={(e) => setAuctionForm({ ...auctionForm, reservePrice: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="ถ้าไม่มีไม่ต้องใส่"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    {/* Auction Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ประเภทประมูล *
                      </label>
                      <select
                        value={auctionForm.auctionType}
                        onChange={(e) => setAuctionForm({ ...auctionForm, auctionType: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        required
                      >
                        <option value="highest_bid">เสนอราคาสูงสุด</option>
                      </select>
                    </div>

                    {/* Duration */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ระยะเวลา (ชั่วโมง) *
                      </label>
                      <input
                        type="number"
                        value={auctionForm.duration}
                        onChange={(e) => setAuctionForm({ ...auctionForm, duration: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        min="1"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 mt-6">
                    <button
                      type="submit"
                      className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                    >
                      เปิดประมูล ({MARKETPLACE_FEES.CREATE_AUCTION.default} เหรียญ)
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateAuctionForm(false);
                        setSelectedProductForAuction(null);
                        setAuctionForm({
                          startingPrice: '',
                          reservePrice: '',
                          auctionType: 'highest_bid',
                          duration: 24
                        });
                      }}
                      className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-100"
                    >
                      ยกเลิก
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Buyer Requests Tab */}
        {activeTab === 'buyerRequests' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">ประกาศหาสินค้าของฉัน</h2>
              <button
                onClick={() => setShowCreateBuyerRequestForm(true)}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
              >
                <span>+</span>
                <span>สร้างประกาศใหม่ ({MARKETPLACE_FEES.CREATE_BUYER_REQUEST.default} เหรียญ)</span>
              </button>
            </div>

            {buyerRequests.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">📢</div>
                <p className="text-gray-600">ยังไม่มีประกาศ</p>
              </div>
            ) : (
              <div className="space-y-4">
                {buyerRequests.map((request) => {
                  const endDate = request.endDate?.toDate?.() || new Date(request.endDate);
                  const now = new Date();
                  const isActive = endDate > now && request.status === 'active';
                  const timeLeft = endDate - now;
                  const daysLeft = Math.floor(timeLeft / 86400000);
                  
                  return (
                    <div
                      key={request.id}
                      className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-gray-800">
                              {request.productName || 'สินค้า'}
                            </h3>
                            {isActive ? (
                              <span className="px-2 py-1 bg-green-100 text-green-600 text-xs rounded-full">
                                กำลังเปิดรับ
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                สิ้นสุดแล้ว
                              </span>
                            )}
                          </div>
                          
                          {request.description && (
                            <p className="text-sm text-gray-600 mb-2">{request.description}</p>
                          )}
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            {request.category && (
                              <div>
                                <p className="text-gray-500">หมวดหมู่</p>
                                <p className="font-semibold text-gray-800">{request.category}</p>
                              </div>
                            )}
                            {request.quantity && (
                              <div>
                                <p className="text-gray-500">จำนวน</p>
                                <p className="font-semibold text-gray-800">
                                  {request.quantity} {request.unit || 'กรัม'}
                                </p>
                              </div>
                            )}
                            {request.desiredPrice && (
                              <div>
                                <p className="text-gray-500">ราคาที่ต้องการ</p>
                                <p className="font-semibold text-gray-800">
                                  ฿{request.desiredPrice.toLocaleString()}
                                </p>
                              </div>
                            )}
                            <div>
                              <p className="text-gray-500">เวลาที่เหลือ</p>
                              <p className="font-semibold text-gray-800">
                                {isActive ? (
                                  daysLeft > 0 ? `${daysLeft} วัน` : 'น้อยกว่า 1 วัน'
                                ) : (
                                  'สิ้นสุดแล้ว'
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Create Buyer Request Form Modal */}
        {showCreateBuyerRequestForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800">สร้างประกาศหาสินค้า</h2>
                  <button
                    onClick={() => {
                      setShowCreateBuyerRequestForm(false);
                      setBuyerRequestForm({
                        productName: '',
                        description: '',
                        category: '',
                        quantity: '',
                        unit: 'กรัม',
                        desiredPrice: '',
                        duration: 7
                      });
                    }}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ✕
                  </button>
                </div>

                <form onSubmit={handleCreateBuyerRequest}>
                  <div className="space-y-4">
                    {/* Product Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ชื่อสินค้า *
                      </label>
                      <input
                        type="text"
                        value={buyerRequestForm.productName}
                        onChange={(e) => setBuyerRequestForm({ ...buyerRequestForm, productName: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="เช่น กัญชาสายพันธุ์..."
                        required
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        รายละเอียด *
                      </label>
                      <textarea
                        value={buyerRequestForm.description}
                        onChange={(e) => setBuyerRequestForm({ ...buyerRequestForm, description: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        rows="3"
                        placeholder="อธิบายรายละเอียดสินค้าที่ต้องการ..."
                        required
                      />
                    </div>

                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        หมวดหมู่ *
                      </label>
                      <select
                        value={buyerRequestForm.category}
                        onChange={(e) => setBuyerRequestForm({ ...buyerRequestForm, category: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        required
                      >
                        <option value="">เลือกหมวดหมู่</option>
                        <option value="Flowers">Flowers</option>
                        <option value="Seeds">Seeds</option>
                        <option value="Edibles">Edibles</option>
                        <option value="Pre Rolls">Pre Rolls</option>
                        <option value="Oils">Oils</option>
                        <option value="CBD">CBD</option>
                      </select>
                    </div>

                    {/* Quantity and Unit */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          จำนวน *
                        </label>
                        <input
                          type="number"
                          value={buyerRequestForm.quantity}
                          onChange={(e) => setBuyerRequestForm({ ...buyerRequestForm, quantity: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          หน่วย *
                        </label>
                        <select
                          value={buyerRequestForm.unit}
                          onChange={(e) => setBuyerRequestForm({ ...buyerRequestForm, unit: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          required
                        >
                          <option value="กรัม">กรัม</option>
                          <option value="กิโลกรัม">กิโลกรัม</option>
                          <option value="ชิ้น">ชิ้น</option>
                          <option value="แพ็ค">แพ็ค</option>
                        </select>
                      </div>
                    </div>

                    {/* Desired Price */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ราคาที่ต้องการ (ไม่บังคับ)
                      </label>
                      <input
                        type="number"
                        value={buyerRequestForm.desiredPrice}
                        onChange={(e) => setBuyerRequestForm({ ...buyerRequestForm, desiredPrice: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="ถ้าไม่มีไม่ต้องใส่"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    {/* Duration */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ระยะเวลา (วัน) *
                      </label>
                      <input
                        type="number"
                        value={buyerRequestForm.duration}
                        onChange={(e) => setBuyerRequestForm({ ...buyerRequestForm, duration: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        min="1"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 mt-6">
                    <button
                      type="submit"
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                    >
                      สร้างประกาศ ({MARKETPLACE_FEES.CREATE_BUYER_REQUEST.default} เหรียญ)
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateBuyerRequestForm(false);
                        setBuyerRequestForm({
                          productName: '',
                          description: '',
                          category: '',
                          quantity: '',
                          unit: 'กรัม',
                          desiredPrice: '',
                          duration: 7
                        });
                      }}
                      className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-100"
                    >
                      ยกเลิก
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div>
            {/* Print Store Label Button */}
            <div className="mb-6">
              <button
                onClick={() => setShowStoreLabelModal(true)}
                className="w-full md:w-auto px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                🖨️ พิมพ์ใบปะหน้าของร้านค้า
              </button>
            </div>

            <StoreSettingsForm 
              store={store} 
              onUpdate={async (storeData) => {
                try {
                  // ถ้ามี packageType ใน storeData แสดงว่าเป็นการอัพเดท Package
                  // ไม่ต้องเรียก updateStore เพราะ subscribeToPackage ทำไปแล้ว
                  if (storeData.packageType) {
                    // เป็นการอัพเดท Package - refresh store data เท่านั้น
                    await fetchStore();
                  } else {
                    // เป็นการอัพเดทข้อมูลร้านปกติ
                    await updateStore(store.id, storeData);
                    alert('บันทึกข้อมูลร้านสำเร็จ!');
                    await fetchStore();
                  }
                } catch (error) {
                  alert('เกิดข้อผิดพลาด: ' + error.message);
                }
              }}
            />
          </div>
        )}

        {/* Store Label Print Modal */}
        {showStoreLabelModal && (
          <StoreLabelPrintModal
            isOpen={showStoreLabelModal}
            onClose={() => setShowStoreLabelModal(false)}
            store={store}
            packageType={packageType}
          />
        )}

        {/* Shipping Label Print Modal */}
        {showShippingLabelModal && selectedOrderForPrint && (
          <ShippingLabelPrintModal
            isOpen={showShippingLabelModal}
            onClose={() => {
              setShowShippingLabelModal(false);
              setSelectedOrderForPrint(null);
            }}
            order={selectedOrderForPrint}
            store={store}
            packageType={packageType}
          />
        )}

        {/* Packing Slip Print Modal */}
        {showPackingSlipModal && (
            <PackingSlipPrintModal
              isOpen={showPackingSlipModal}
              onClose={() => {
                setShowPackingSlipModal(false);
                setSelectedOrderForPrint(null);
                setSelectedOrdersForPackingSlip([]);
              }}
              order={selectedOrderForPrint}
              orders={selectedOrdersForPackingSlip.length > 0 
                ? orders.filter(o => selectedOrdersForPackingSlip.includes(o.id))
                : null}
              store={store}
              packageType={packageType}
            />
        )}
      </div>
    </div>
  );
};

export default StoreManagement;

